/**
 * #777 — glanceable, ticker-safe navigation to the lines where a trader has a
 * working order resting.
 *
 * The desk reads "open position" as an order still on the book — the thing
 * that needs watching — not as filled contracts. #773 had written out held
 * lines here; that was the inverse of the request. Held contracts stay visible
 * through the C ring, and this list names only lines with open quantity.
 *
 * Each value is still a real button so choosing it resolves the exact ticker,
 * follows the strip's perspective label, and is fenced from A/D hotkeys. The
 * list wraps inside its own cell rather than widening the table when several
 * lines carry orders.
 */
import { cn } from '@/lib/infra/utils';
import { isReachable, type StripActivityLine } from './stripActivity';

export interface StripWorkingOrderLinksProps {
  /** Working-order lines only, ascending. */
  lines: readonly StripActivityLine[];
  /** The exact label rendered by the corresponding strip chip. */
  labelFor: (line: StripActivityLine) => string;
  /** 'Spread' | 'Total', used in accessible names. */
  kindLabel: string;
  /** Select by ticker; never by display value or a captured array index. */
  onSelect: (ticker: string) => void;
  /** Match a direct strip-chip selection by taking only a free hotkey focus. */
  onFocusCard?: () => void;
}

export function StripWorkingOrderLinks({
  lines,
  labelFor,
  kindLabel,
  onSelect,
  onFocusCard,
}: StripWorkingOrderLinksProps) {
  if (lines.length === 0) return null;

  return (
    <span
      className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-x-1 gap-y-0.5"
      aria-label={`${kindLabel} lines with working orders`}
      data-order-lines=""
    >
      {lines.map((line) => {
        const reachable = isReachable(line);
        const label = labelFor(line);
        const title = reachable
          ? `Select ${label}; you have a working order on this ${kindLabel.toLowerCase()} line`
          : `You have a working order on ${label}, but this line is not listed on the board`;

        if (!reachable) {
          return (
            <span
              key={line.ticker}
              className="text-muted-foreground/60 whitespace-nowrap font-mono text-[10px] tabular-nums tracking-tight"
              title={title}
              data-order-line={line.ticker}
            >
              {label}
            </span>
          );
        }

        return (
          <button
            key={line.ticker}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(line.ticker);
              onFocusCard?.();
            }}
            title={title}
            aria-label={`Select ${label} ${kindLabel.toLowerCase()} line with working order`}
            className={cn(
              'text-muted-foreground/80 hover:text-foreground whitespace-nowrap rounded-sm',
              'font-mono text-[10px] tabular-nums tracking-tight transition-colors',
              'focus-visible:ring-primary/70 focus-visible:outline-none focus-visible:ring-2',
            )}
            data-order-line={line.ticker}
          >
            {label}
          </button>
        );
      })}
    </span>
  );
}
